import { type ReactNode, type RefObject, useCallback, useEffect, useRef, useState } from 'react';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  size?: 'sm' | 'md' | 'lg';
  children: ReactNode;
  footer?: ReactNode;
  /**
   * `alertdialog` for an interruption that needs a response (e.g. the client portal's
   * inactivity warning). Defaults to `dialog`.
   */
  role?: 'dialog' | 'alertdialog';
  /** Element to focus on open. Defaults to the first focusable element (the close button). */
  initialFocusRef?: RefObject<HTMLElement | null>;
  /** Id of the element that describes the dialog, for `aria-describedby`. */
  describedById?: string;
}

const SIZE_CLASS: Record<string, string> = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-3xl',
};

/**
 * Everything the browser will move focus to with Tab. `[disabled]` and
 * `aria-hidden` elements are excluded because they are skipped by the real tab
 * order too — including them would make the wrap land on a dead element.
 *
 * Deliberately NOT filtered on `offsetParent`/`getClientRects` (the usual
 * "is it visible" test): jsdom lays nothing out, so every element reports zero
 * size and such a filter empties the list under test — the trap would then pass
 * its tests by doing nothing at all.
 */
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export function Modal({ open, onClose, title, size = 'md', children, footer, role = 'dialog', initialFocusRef, describedById }: ModalProps) {
  const [visible, setVisible] = useState(false);
  const [animating, setAnimating] = useState(false);
  const backdropRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // What had focus before we opened, so it can be handed back on close.
  const restoreRef = useRef<HTMLElement | null>(null);

  // Open: mount then animate in
  useEffect(() => {
    if (open) {
      setAnimating(true);
      // Force a reflow before adding visible class
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true));
      });
    } else if (visible) {
      // Close: animate out then unmount
      setVisible(false);
      const timer = setTimeout(() => setAnimating(false), 150);
      return () => clearTimeout(timer);
    }
    // `visible` intentionally omitted: it is read as a guard, not tracked. Adding
    // it makes the close path cancel itself — setVisible(false) retriggers the
    // effect, whose cleanup clearTimeout()s the timer that sets animating=false,
    // so `animating` sticks true and `if (!open && !animating)` never returns
    // null. A closed Modal would stay mounted as an invisible fixed inset-0
    // z-50 overlay, swallowing clicks page-wide. Guarded by test/Modal.test.tsx.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Escape key
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const focusables = useCallback(
    () => Array.from(panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []),
    [],
  );

  /**
   * Move focus INTO the dialog on open, and hand it back on close.
   *
   * WCAG 2.1 AA (#10803 AC6). Without this the dialog opens with focus still on
   * the trigger behind it, so a keyboard or screen-reader user tabs through the
   * page underneath a modal they cannot see — the content is visually covered but
   * still in the tab order, which is worse than no dialog at all.
   */
  useEffect(() => {
    if (!open) return;
    restoreRef.current = (document.activeElement as HTMLElement | null) ?? null;
    // Captured here rather than read in the cleanup: by cleanup time the ref may
    // already point elsewhere (or at a detached node), so the cleanup has to reason
    // about the panel THIS effect opened.
    const panel = panelRef.current;
    // The panel itself is tabindex=-1, so it can hold focus when a dialog has no
    // focusable content of its own (a plain "no help for this page yet" message).
    const target = initialFocusRef?.current ?? focusables()[0] ?? panel;
    target?.focus();
    return () => {
      // Only take focus back if it is still inside the dialog we are closing, or has
      // fallen to <body> because that dialog was removed. If something else has
      // deliberately claimed focus, stealing it back is a bug.
      const active = document.activeElement as HTMLElement | null;
      if (!panel || !active || panel.contains(active) || active === document.body) {
        restoreRef.current?.focus?.();
      }
    };
  }, [open, focusables, initialFocusRef]);

  /**
   * Focus trap — Tab and Shift+Tab wrap within the dialog rather than escaping to
   * the page behind it. `aria-modal` alone does NOT do this: it is a hint to
   * assistive tech, not behaviour the browser enforces.
   */
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;

      const items = focusables();
      // Nothing focusable inside: keep focus on the panel rather than letting Tab
      // walk out into the page behind the backdrop.
      if (items.length === 0) {
        e.preventDefault();
        panel.focus();
        return;
      }

      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement as HTMLElement | null;
      const inside = !!active && panel.contains(active) && active !== panel;

      if (e.shiftKey) {
        if (!inside || active === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (!inside || active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, focusables]);

  if (!open && !animating) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role={role}
      aria-modal="true"
      aria-label={title}
      aria-describedby={describedById}
    >
      {/* Backdrop */}
      <div
        ref={backdropRef}
        className={`absolute inset-0 bg-black transition-opacity duration-200 ${visible ? 'opacity-50' : 'opacity-0'}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal panel */}
      <div
        ref={panelRef}
        tabIndex={-1}
        className={`relative bg-[var(--surface)] rounded-xl shadow-xl ${SIZE_CLASS[size] ?? SIZE_CLASS.md} w-full mx-4 max-h-[90vh] flex flex-col transition-all duration-200 ease-out focus:outline-none ${
          visible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)] shrink-0">
          <h2 className="text-lg font-semibold text-[var(--text1)]">{title}</h2>
          <button
            onClick={onClose}
            className="text-[var(--text3)] hover:text-[var(--text1)] transition-colors p-1 -mr-1"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body — scrollable */}
        <div className="px-6 py-5 overflow-y-auto flex-1">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[var(--border)] shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
